import axios, { AxiosResponse } from 'axios';
import { OnboardingForm, PreferredDisplayMode } from 'components/Onboarding/OnboardingFormClass';
import { IInvoiceEntity } from 'components/Invoicing/Configuration/InvoiceDisplay';
import {
 IOnboardingOption, IUPPTheme, ITransactionType, IMenuConfig,
} from 'interfaces';
import { IInvoiceDetailsForm } from 'components/Invoicing/Configuration/UpdateCard/types';
import { IProvider } from 'components/PaymentMethods/types/types';
import {
  IPaymentProvider,
  ICreatePaymentProviderForm,
} from 'routes/operations/payment-provider.interface';
import {
  HierarchyLevel,
  IOnboardFamilyEntityDto,
  IContactDetailsDto,
  IMerchantDetailsDto,
  CountryCode,
  IUserDetailsDto,
} from 'components/Onboarding/types/onboarding.dto';
import { IFilter } from 'components/Reports/TransactionReportPage';
import { Article, IPressroomPost } from 'components/WebsiteContent/types';
import { notifyError } from 'libs/utils/Toaster';
import storageService from './storage.service';
import { IUser } from './auth.service';

const nullStr = (val: any): any => (val === 'NULL' || val === 'null' || val === '' ? null : val);

export interface IBackendError {
  message: string,
  code?: string,
}

// Normalizes an axios error into a plain { message, code } object so callers
// can branch on a machine-readable `code` (e.g. XERO_REAUTH_REQUIRED) instead
// of string-matching on a message meant for display.
const toBackendError = (err: any, fallbackMessage: string): IBackendError => {
  const errorData = err?.response?.data;
  if (errorData && typeof errorData === 'object') {
    return { message: errorData.message || fallbackMessage, code: errorData.code };
  }
  return { message: err?.message || fallbackMessage };
};

// Helper to strip non-DTO fields and format the payload
const sanitizeInvoiceDetails = (form: any): any => {
  const sanitized: any = {
    name: form.name,
    invoicingCurrency: nullStr(form.invoicingCurrency),
    transactionCurrency: nullStr(form.transactionCurrency),
    enabledTransactionTypeKeys: form.enabledTransactionTypeKeys,
    gstApplied: form.gstApplied,
    minTransactionCharged: form.minTransactionCharged,
    transactionPricing: form.transactionPricing,
    xeroContactId: nullStr(form.xeroContactId),
    minMonthlyFeeEnabled: form.minMonthlyFeeEnabled ?? false,
    minMonthlyFeeAmount: form.minMonthlyFeeAmount != null ? Number(form.minMonthlyFeeAmount) : null,
    minMonthlyFeeCurrency: nullStr(form.minMonthlyFeeCurrency),
    platformFeeEnabled: form.platformFeeEnabled ?? false,
    platformFeeAmount: form.platformFeeAmount != null ? Number(form.platformFeeAmount) : null,
    platformFeeCurrency: nullStr(form.platformFeeCurrency),
    bipsCapEnabled: form.bipsCapEnabled ?? false,
    bipsCapAmount: form.bipsCapEnabled && form.bipsCapAmount != null ? Number(form.bipsCapAmount) : null,
    tierBasis: nullStr(form.tierBasis),
  };

  if (Array.isArray(sanitized.transactionPricing)) {
    sanitized.transactionPricing = sanitized.transactionPricing.map((item: any) => ({
      cost: typeof item.cost === 'string' ? parseFloat(item.cost) : item.cost,
      bips: typeof item.bips === 'string' ? parseFloat(item.bips) : (item.bips ?? 0),
      minRange: item.minRange !== null && item.minRange !== undefined ? parseInt(item.minRange, 10) : null,
      maxRange: item.maxRange !== null && item.maxRange !== undefined ? parseInt(item.maxRange, 10) : null,
      transactionTypeKey: item.transactionTypeKey,
      paymentMethodId: item.paymentMethodId ?? null,
    }));
  }

  return sanitized;
};

const adminApi = axios.create({
  baseURL: process.env.REACT_APP_OPTTY_ADMIN_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

adminApi.interceptors.request.use(
  (config) => {
    if (config.headers && !config.headers.Authorization) {
      const jwt = storageService.getJwtCookie();
      if (jwt) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${jwt}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

export { adminApi };

const toPressroomFormData = (article: Article, image: File | null | undefined, includeEmpty: boolean) => {
  const fd = new FormData();
  if (article.slug || includeEmpty) fd.append('slug', article.slug);
  fd.append('tag', article.tag);
  fd.append('date', article.date);
  fd.append('readTime', article.readTime);
  fd.append('title', article.title);
  fd.append('excerpt', article.excerpt);
  if (article.thumbnail || includeEmpty) fd.append('thumbnail', article.thumbnail);
  if (article.externalPostLink || includeEmpty) fd.append('externalPostLink', article.externalPostLink);
  fd.append('body', JSON.stringify(article.body));
  if (image) fd.append('image', image);
  return fd;
};

export default {
  async getMenu(): Promise<any[]> {
    return adminApi
      .get('/menu')
      .then(async (res: AxiosResponse<any[]>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getMenu api service  ${err}`);
        notifyError('Failed to fetch menu. Please try again later.');
        throw err;
      });
  },

  async getMenuConfigTree(entityId: string): Promise<any[]> {
    return adminApi
      .get(`/menu/config/tree/${entityId}`)
      .then(async (res: AxiosResponse<any[]>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getMenuConfigTree api service  ${err}`);
        notifyError('Failed to fetch menu. Please try again later.');
        throw err;
      });
  },

  async bulkUpdateMenuConfig(
    entityId: string,
    configs: {
      menuId: string;
      consumer: string;
      visibility: boolean;
      label?: string;
      order?: number;
    }[],
  ): Promise<any> {
    // Always send with entityId in the payload structure
    try {
      const res = await adminApi.post('/menu/config/bulk', { entityId, configs });
      if (res.status >= 200 && res.status < 300) {
        return res.data;
      }
      throw new Error(res.statusText);
    } catch (err) {
      console.error('Error in bulkUpdateMenuConfig:', err);
      throw err;
    }
  },

  async getUnbillableMerchants(month: number, year: number): Promise<any[]> {
    return adminApi
      .get('/invoicing/unbillable-merchants', {
        params: { month, year },
      })
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        if (err.response && err.response.data) {
          const errorData = err.response.data;
          if (typeof errorData === 'object' && errorData !== null) {
            if (errorData.message && typeof errorData.message === 'string') {
              throw errorData.message;
            }
            throw JSON.stringify(errorData);
          }
          throw errorData;
        }
        throw (
          err.message
          || 'An unknown error occurred while checking for unbillable merchants.'
        );
      });
  },

  // Xero auth is established separately via authenticateXeroSession before
  // this is called, so no code/redirectUri is sent here — the backend
  // reuses the cached session token for the admin.
  async generateInvoice(month: number, year: number) {
    return adminApi
      .put('/invoicing/generate', {
        month,
        year,
      })
      .catch((err) => {
        throw toBackendError(err, 'An unknown error occurred during invoice generation.');
      });
  },

  // Exchanges a Xero OAuth code immediately, ahead of month/year selection —
  // the code expires in ~60s and won't survive waiting on the generate form.
  async authenticateXeroSession(redirectUri: string, code: string): Promise<{ authenticated: boolean }> {
    return adminApi
      .put('/invoicing/xero/session', { redirectUri, code })
      .then((res: AxiosResponse<{ authenticated: boolean }>) => res.data)
      .catch((err) => {
        throw toBackendError(err, 'Failed to connect to Xero.');
      });
  },

  async removePaymentMethod(brandId: string): Promise<void> {
    return adminApi
      .delete(`/payment-methods/brand/${brandId}`)
      .then(async (res: AxiosResponse<void>) => {
        if (res.status >= 200 && res.status < 300) {
          return;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in post payment method api service  ${err}`);
        throw err;
      });
  },

  async onboardFamilyEntity(
    onboardingForm: OnboardingForm,
    hasParent: boolean,
  ): Promise<any> {
    const {
      redirectUrl: formRedirectUrl,
      name,
      parentId,
      hierarchyLevel,
      logoFile,
      logoUrl,
      isAllOptIn,
      leftSidebarBgColour,
      leftSidebarActiveColour,
      leftSidebarHoverColour,
      leftSidebarTextColour,
      topBarTextColour,
      topBarBgColour,
      countryOfCompany,
      financeEmail,
      companyNumber,
      userFirstName,
      userLastName,
      userEmail,
      userPassword,
      userConfirmPassword,
      userSubRoles,
      themeCustomisationEnabled,
      partnerMerchantId,
      preferredDisplayMode,
      paymentLinkMenuAlias,
      popupTitle,
      popupMessage,
    } = onboardingForm;

    let redirectUrl = formRedirectUrl;
    if (redirectUrl && !/^https?:\/\//i.test(redirectUrl)) {
      redirectUrl = `https://${redirectUrl}`;
    }

    const merchantDetails: IMerchantDetailsDto = {
      parentId: hasParent ? parentId : null,
      hierarchyLevel: hierarchyLevel as HierarchyLevel,
      country: countryOfCompany as CountryCode,
      isAllOptIn,
      redirectUrl,
    };

    // Only include theme if theme customization is enabled
    if (themeCustomisationEnabled) {
      const theme: IUPPTheme = {
        logo: logoUrl,
        leftSidebarBgColor: leftSidebarBgColour,
        leftSidebarActiveColor: leftSidebarActiveColour,
        leftSidebarHoverColor: leftSidebarHoverColour,
        leftSidebarTextColor: leftSidebarTextColour,
        topBarBgColor: topBarBgColour,
        topBarTextColor: topBarTextColour,
      };
      if (hierarchyLevel === HierarchyLevel.Merchant && paymentLinkMenuAlias) {
        theme.paymentLinkMenuAlias = paymentLinkMenuAlias;
      }
      merchantDetails.theme = theme;
    }

    if (hierarchyLevel === HierarchyLevel.Merchant) {
      if (partnerMerchantId) {
        merchantDetails.partnerMerchantId = partnerMerchantId;
      }
      merchantDetails.preferredDisplayMode = preferredDisplayMode;
      if (preferredDisplayMode === PreferredDisplayMode.IN_CONTEXT) {
        merchantDetails.popupOverlayConfig = {
          title: popupTitle,
          message: popupMessage,
        };
      }
    }

    const contactDetails: IContactDetailsDto = {
      financeEmail,
      companyNumber,
    };

    const userDetails: IUserDetailsDto = {
      userEmail,
      firstName: userFirstName,
      lastName: userLastName,
      password: userPassword,
      confirmPassword: userConfirmPassword,
      subRoles: userSubRoles,
    };

    const parsed: IOnboardFamilyEntityDto = {
      name,
      merchantDetails,
      contactDetails,
      userDetails,
    };

    // Multipart/form-data — mirrors createAnnouncement's pattern. Nested objects
    // travel as JSON strings; the backend parses/validates each one individually.
    const fd = new FormData();
    fd.append('name', parsed.name);
    fd.append('merchantDetails', JSON.stringify(parsed.merchantDetails));
    fd.append('contactDetails', JSON.stringify(parsed.contactDetails));
    fd.append('userDetails', JSON.stringify(parsed.userDetails));
    if (logoFile) {
      fd.append('image', logoFile);
    }

    return adminApi.post('/family', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((res) => {
      if (res.status >= 200 && res.status < 300) {
        return res.data;
      }
      throw res.data;
    });
  },

  async getFamilyList(): Promise<IOnboardingOption[]> {
    return adminApi
      .get('/family/children')
      .then((res: AxiosResponse<IOnboardingOption[]>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getFamilyList api service  ${err}`);
        throw err;
      });
  },

  async getOrderDetails(merchantReference: string, merchantId: string) {
    return adminApi
      .get(`orders/${merchantReference}/details?merchantId=${merchantId}`)
      .then(async (res: AxiosResponse<any>) => {
        if (res.status === 200) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in order details api service  ${err}`);
        throw err;
      });
  },

  async updateInvoiceDetails(form: IInvoiceDetailsForm, invoicingId: string) {
    const body = sanitizeInvoiceDetails(form);
    return adminApi
      .put(`/invoicing/details/${invoicingId}`, body)
      .then(async (res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((error) => {
        throw error?.response?.data || error;
      });
  },

  async createInvoiceDetails(form: IInvoiceDetailsForm, familyId: string) {
    const body = sanitizeInvoiceDetails(form);
    return adminApi
      .post(`/invoicing/details/${familyId}`, body)
      .then(async (res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((error) => {
        throw error?.response?.data || error;
      });
  },

  async getInvoiceDetails(invoicingId: string): Promise<IInvoiceEntity> {
    return adminApi
      .get(`/invoicing/details/${invoicingId}`)
      .then(async (res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getInvoiceDetails api service  ${err}`);
        throw err;
      });
  },

  async getTransactionTypes(): Promise<ITransactionType[]> {
    return adminApi
      .get('/invoicing/transaction-types')
      .then(async (res: AxiosResponse<ITransactionType[]>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getTransactionTypes api service  ${err}`);
        throw err;
      });
  },

  async getCommercialModelSummary(): Promise<{
    configuredPartners: number;
    totalPartners: number;
    minMonthlyFeeCount: number;
    platformFeeCount: number;
    systemDefaultBps: number;
  }> {
    return adminApi
      .get('/invoicing/commercial-model/summary')
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      });
  },

  async getCommercialModelData(): Promise<any[]> {
    return adminApi
      .get('/invoicing/commercial-model/data')
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      });
  },

  async getCommercialModelUnconfigured(): Promise<{ id: string; name: string; hierarchyLevel: string; isArchived?: boolean }[]> {
    return adminApi
      .get('/invoicing/commercial-model/unconfigured')
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      });
  },

  setHierarchyLevelArchiveStatus(hierarchyLevel: 'grandparent' | 'parent' | 'merchant') {
    return (entityId: string, isArchived: boolean) => adminApi
      .patch(`/invoicing/commercial-model/${hierarchyLevel}s/${entityId}/archive-status`, { isArchived })
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      })
      .catch((error) => {
        throw error?.response?.data || error;
      });
  },

  async getInvoicableFamilies(): Promise<IInvoiceEntity[]> {
    return adminApi
      .get('/invoicing/billable')
      .then(async (res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data.map((family: any) => {
            const map = { ...family };
            map.familyId = family.childId || family.parentId || family.grandparentId;
            if (family.invoicingDetailsId) {
              map.id = family.invoicingDetailsId;
            }
            map.txnCost = family.transactionPricing;
            delete map.transactionPricing;
            if (family.currency) {
              map.transactionCurrency = family.currency;
              delete map.currency;
            }
            return map;
          });
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getInvoicableFamilies api service  ${err}`);
        throw err;
      });
  },

  async getInvoicableFamiliesMissing(): Promise<
    {
      name: string;
      grandparentId: string | null;
      parentId: string | null;
      childId: string | null;
    }[]
  > {
    return adminApi
      .get('/invoicing/billable/missing')
      .then(async (res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) {
          // Add a consistent familyId for lookups
          return res.data.map((family: any) => ({
            ...family,
            familyId: family.childId || family.parentId || family.grandparentId,
          }));
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getInvoicableFamilies api service  ${err}`);
        throw err;
      });
  },

  async getMerchants(): Promise<IOnboardingOption[]> {
    return adminApi
      .get('/family/children/hierarchy')
      .then(async (res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getMerchants api service  ${err}`);
        throw err;
      });
  },

  async getAuditLog(): Promise<any> {
    return adminApi
      .get('/auditLog')
      .then(async (res) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getAuditLog api service  ${err}`);
        throw err;
      });
  },

  async getTransReport(filter: IFilter) {
    const { currency, ...rest } = filter;
    const payload = currency ? filter : rest;

    return adminApi
      .post('reports/get-transaction-wise-report', payload)
      .then(async (res) => {
        console.log(`getTransReport 1==>: ${JSON.stringify(res)}`);
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in set paid commission  ${err}`);
        throw err;
      });
  },

  async getPaymentMethodProviders(): Promise<IProvider[]> {
    return adminApi
      .get('/payment-methods/providers')
      .then(async (res) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getPaymentMethodBrands api service  ${err}`);
        throw err;
      });
  },

  async getParents(): Promise<any> {
    return adminApi
      .get('/family/parents/hierarchy')
      .then(async (res) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getParents api service  ${err}`);
        throw err;
      });
  },

  async getMerchantsByParentId(parentId: string): Promise<IOnboardingOption[]> {
    return adminApi
      .get(`/family/parents/${parentId}/children`)
      .then(async (res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getMerchantsByParentId api service  ${err}`);
        throw err;
      });
  },

  async getGrandparents(): Promise<any> {
    return adminApi
      .get('/family/grandparents')
      .then(async (res) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getGrandparents api service  ${err}`);
        throw err;
      });
  },

  async getCountryCodes(): Promise<string[]> {
    return adminApi
      .get('/family/country-codes')
      .then(async (res) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getCountryCodes api service  ${err}`);
        throw err;
      });
  },

  async getFamilyEntityDetails(entityId: string): Promise<any> {
    return adminApi
      .get(`/family/${entityId}`)
      .then(async (res) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`error thrown in getFamilyEntityDetails api service  ${err}`);
        throw err;
      });
  },

  // check authenticated
  async checkAuthenticated(jwt: string): Promise<boolean> {
    return adminApi
      .get('/auth', {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      })
      .then((res) => {
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error('Authentication failed');
      })
      .then((data: string) => {
        const isAuthenticated = Object.keys(data).length !== 0;
        return isAuthenticated;
      })
      .catch((error) => {
        console.error('Error occurred:', error);
        return false;
      });
  },

  // login
  async login(jwt: string): Promise<IUser | undefined> {
    return adminApi
      .get('/auth/login', {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      })
      .then((res) => {
        console.log(`res in login: ${res}`);
        if (res.status >= 200 && res.status < 300) {
          return res.data;
        }
        throw new Error(`Authentication failed: ${res.statusText}`);
      })
      .catch((error) => {
        console.error('Error occurred unauthed:', error);
        return undefined;
      });
  },

  async getReferralAgreements(): Promise<any[]> {
    try {
      const response = await adminApi.get('/invoicing/referrals/agreements');
      return response.data;
    } catch (error) {
      console.error('Error fetching referral agreements:', error);
      throw error;
    }
  },

  async getReferralAgreementById(id: string): Promise<any> {
    try {
      const response = await adminApi.get(`/invoicing/referrals/agreements/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching referral agreement with id ${id}:`, error);
      throw error;
    }
  },

  async createReferralAgreement(agreement: any): Promise<any> {
    try {
      const response = await adminApi.post('/invoicing/referrals/agreements', agreement);
      return response.data;
    } catch (error) {
      console.error('Error creating referral agreement:', error);
      throw error;
    }
  },

  async updateReferralAgreement(id: string, agreement: any): Promise<any> {
    try {
      const response = await adminApi.put(
        `/invoicing/referrals/agreements/${id}`,
        agreement,
      );
      return response.data;
    } catch (error) {
      console.error('Error updating referral agreement:', error);
      throw error;
    }
  },

  async getExclusionsForAgreement(agreementId: string): Promise<any[]> {
    try {
      const response = await adminApi.get(
        `/invoicing/referrals/agreements/${agreementId}/exclusions`,
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching exclusions for agreement ${agreementId}:`, error);
      throw error;
    }
  },

  async createExclusion(agreementId: string, exclusionData: any): Promise<any> {
    try {
      const response = await adminApi.post(
        `/invoicing/referrals/agreements/${agreementId}/exclusions`,
        exclusionData,
      );
      return response.data;
    } catch (error) {
      console.error(`Error creating exclusion for agreement ${agreementId}:`, error);
      throw error;
    }
  },

  async deleteExclusion(exclusionId: string): Promise<void> {
    try {
      await adminApi.delete(`/invoicing/referrals/exclusions/${exclusionId}`);
    } catch (error) {
      console.error(`Error deleting exclusion ${exclusionId}:`, error);
      throw error;
    }
  },

  async getReferralEntityConfigs(agreementId: string): Promise<any[]> {
    try {
      const response = await adminApi.get(`/invoicing/referrals/agreements/${agreementId}/entity-configs`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching entity configs for agreement ${agreementId}:`, error);
      throw error;
    }
  },

  async upsertReferralEntityConfig(agreementId: string, config: any): Promise<any> {
    try {
      const payload = {
        ...config,
        flatBips: Math.round(Number(config.flatBips ?? 0)),
        tiers: Array.isArray(config.tiers)
          ? config.tiers.map((t: any) => ({
              min: Number(t.min ?? 0),
              max: t.max === '' || t.max === null || t.max === undefined ? null : Number(t.max),
              bips: Math.round(Number(t.bips ?? 0)),
            }))
          : null,
      };
      const response = await adminApi.post(`/invoicing/referrals/agreements/${agreementId}/entity-configs`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error upserting entity config for agreement ${agreementId}:`, error);
      throw error;
    }
  },

  async updateReferralEntityConfig(configId: string, config: any): Promise<any> {
    try {
      const payload = {
        ...config,
        flatBips: Math.round(Number(config.flatBips ?? 0)),
        tiers: Array.isArray(config.tiers)
          ? config.tiers.map((t: any) => ({
              min: Number(t.min ?? 0),
              max: t.max === '' || t.max === null || t.max === undefined ? null : Number(t.max),
              bips: Math.round(Number(t.bips ?? 0)),
            }))
          : null,
      };
      const response = await adminApi.put(`/invoicing/referrals/entity-configs/${configId}`, payload);
      return response.data;
    } catch (error) {
      console.error(`Error updating entity config ${configId}:`, error);
      throw error;
    }
  },

  async deleteReferralEntityConfig(configId: string): Promise<void> {
    try {
      await adminApi.delete(`/invoicing/referrals/entity-configs/${configId}`);
    } catch (error) {
      console.error(`Error deleting entity config ${configId}:`, error);
      throw error;
    }
  },

  async getReferralProofs(): Promise<any[]> {
    try {
      const response = await adminApi.get('/invoicing/referrals/proofs');
      return response.data as any[];
    } catch (error) {
      console.error('Error fetching referral proofs:', error);
      throw error;
    }
  },

  async getReferralReportingData(filters: {
    dateFrom?: string;
    dateTo?: string;
    agreementId?: string;
    merchantId?: string;
    parentId?: string;
    grandparentId?: string;
  }): Promise<any> {
    try {
      const params: Record<string, string> = {};
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.agreementId) params.agreementId = filters.agreementId;
      if (filters.merchantId) params.merchantId = filters.merchantId;
      if (filters.parentId) params.parentId = filters.parentId;
      if (filters.grandparentId) params.grandparentId = filters.grandparentId;
      const response = await adminApi.get('/invoicing/referrals/reporting', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching referral reporting data:', error);
      throw error;
    }
  },

  async registerMenuConfig(grandParentId: string, menuConfig: IMenuConfig): Promise<any> {
    try {
      if (Array.isArray(menuConfig)) {
        // backend expects raw array in some deployments
        const response = await adminApi.post('/menu/config/bulk', menuConfig);
        return response.data;
      }

      const response = await adminApi.post('/menu/config/bulk', {
        entityId: grandParentId,
        configs: menuConfig,
      });
      return response.data;
    } catch (error: any) {
      console.error(`Error registering menu config: ${error?.response?.data || error}`);
      throw error?.response?.data || error;
    }
  },

  async fetchAPMConfigurations(
    provider: string,
    currency: string,
    merchantId?: string,
  ): Promise<any> {
    try {
      const response = await adminApi
        .get(`/operations/apm-configurations/${provider}`, {
        params: {
          ...(currency && { currency }),
          ...(merchantId && { merchantId }),
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching APM configurations:', error);
      throw error?.response?.data || error;
    }
  },

  async addOrUpdateAPMConfiguration(
    provider: string,
    currency: string,
    path: string,
    configuration: any,
    merchantId?: string,
  ): Promise<any> {
    try {
      const response = await adminApi.put(
        `/operations/apm-configurations/${provider}/${path}`,
        configuration,
        {
          params: {
            ...(currency && { currency }),
            ...(merchantId && { merchantId }),
          },
        },
      );
      return response.data;
    } catch (error: any) {
      console.error(
        `Error adding/updating APM configuration (${provider}/${path}):`,
        error,
      );
      throw error?.response?.data || error;
    }
  },

  async publishPaymentProvider(providerId: string): Promise<any> {
    try {
      const response = await adminApi.post(
        `/master-payment-providers/publishProvider/${providerId}`,
      );
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error(`Error publishing payment provider ${providerId}:`, error);
      throw error?.response?.data || error;
    }
  },

  async getFeatureMatrixProviders(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    statuses?: string[];
    paymentType?: string;
    isConnectorDirectORVia?: string;
    hasNullStatus?: boolean;
    createOrderAPIImplementationStatus?: string;
    defaultCheckoutContent?: string;
    asyncRefund?: string;
    partialRefund?: string;
    fullRefund?: string;
    asyncFlow?: string;
    cancelOrder?: string;
    autoCapture?: string;
    manualCapture?: string;
    partialCapture?: string;
    multiCapture?: string;
    voidApi?: string;
    partialVoid?: string;
    multiVoid?: string;
    isWebhookImplemented?: string;
    webhookVerification?: string;
    webhookRegistration?: string;
    supportedCurrency?: string;
    paymentBrand?: string;
    sortBy?: string;
    sortOrder?: 'ASC' | 'DESC';
  } = {}): Promise<any> {
    try {
      const response = await adminApi.post('/payment-providers-feature-metrics/getProviders', params);
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error('Error fetching feature matrix providers:', error);
      throw error?.response?.data || error;
    }
  },

  async getFeatureMatrixProviderById(id: string): Promise<any> {
    try {
      const response = await adminApi.get(`/payment-providers-feature-metrics/${id}`);
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error(`Error fetching feature matrix provider ${id}:`, error);
      throw error?.response?.data || error;
    }
  },

  async getFeatureMatrixSupportedCurrencies(): Promise<string[]> {
    try {
      const response = await adminApi.get('/payment-providers-feature-metrics/supported-currencies');
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error('Error fetching supported currencies:', error);
      throw error?.response?.data || error;
    }
  },

  async getFeatureMatrixPaymentBrands(): Promise<string[]> {
    try {
      const response = await adminApi.get('/payment-providers-feature-metrics/payment-brands');
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error('Error fetching payment brands:', error);
      throw error?.response?.data || error;
    }
  },

  async createFeatureMatrixProvider(data: Record<string, any>): Promise<any> {
    try {
      const response = await adminApi.post('/payment-providers-feature-metrics', data);
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error('Error creating feature matrix provider:', error);
      throw error?.response?.data || error;
    }
  },

  async updateFeatureMatrixProvider(id: string, data: Record<string, any>): Promise<any> {
    try {
      const response = await adminApi.patch(`/payment-providers-feature-metrics/${id}`, data);
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error(`Error updating feature matrix provider ${id}:`, error);
      throw error?.response?.data || error;
    }
  },

  async deleteFeatureMatrixProvider(id: string): Promise<void> {
    try {
      await adminApi.delete(`/payment-providers-feature-metrics/${id}`);
    } catch (error: any) {
      console.error(`Error deleting feature matrix provider ${id}:`, error);
      throw error?.response?.data || error;
    }
  },

  // ── Pressroom ───────────────────────────────────────────────────────────────

  async getPressroomPosts(): Promise<IPressroomPost[]> {
    try {
      const res = await adminApi.get('/pressroom');
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error('Error fetching pressroom posts:', error);
      throw error?.response?.data || error;
    }
  },

  // Multipart/form-data (FileInterceptor is active). The uploaded image becomes the post's thumbnail.
  async createPressroomPost(article: Article, image?: File | null): Promise<IPressroomPost> {
    try {
      // Empty slug/thumbnail/external post link are left out: a new post simply has none.
      const res = await adminApi.post('/pressroom', toPressroomFormData(article, image, false), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error('Error creating pressroom post:', error);
      throw error?.response?.data || error;
    }
  },

  // Sends the whole article; an empty slug, thumbnail or external post link clears it. An uploaded image replaces the thumbnail.
  async updatePressroomPost(postId: string, article: Article, image?: File | null): Promise<IPressroomPost> {
    try {
      const res = await adminApi.patch(`/pressroom/${postId}`, toPressroomFormData(article, image, true), {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error(`Error updating pressroom post ${postId}:`, error);
      throw error?.response?.data || error;
    }
  },

  async publishPressroomPost(postId: string): Promise<IPressroomPost> {
    try {
      const res = await adminApi.patch(`/pressroom/${postId}/publish`);
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error(`Error publishing pressroom post ${postId}:`, error);
      throw error?.response?.data || error;
    }
  },

  async unpublishPressroomPost(postId: string): Promise<IPressroomPost> {
    try {
      const res = await adminApi.patch(`/pressroom/${postId}/unpublish`);
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error(`Error unpublishing pressroom post ${postId}:`, error);
      throw error?.response?.data || error;
    }
  },

  // ── Announcements ────────────────────────────────────────────────────────────

  async getAnnouncements(top?: number): Promise<any[]> {
    try {
      const params: Record<string, any> = {};
      if (top) params.top = top;
      const res = await adminApi.get('/announcements', { params });
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error('Error fetching announcements:', error);
      throw error?.response?.data || error;
    }
  },

  // IMPORTANT: create & update MUST use multipart/form-data (FileInterceptor is active)
  async createAnnouncement(data: {
    announcementTitle: string;
    announcementText: string;
    announcementTag: string[];
    announcementBrands: string;
    visibilityRoles: string[];
    image?: File | null;
  }): Promise<any> {
    try {
      const fd = new FormData();
      fd.append('announcementTitle', data.announcementTitle);
      fd.append('announcementText', data.announcementText);
      data.announcementTag.forEach((t) => fd.append('announcementTag', t));
      fd.append('announcementBrands', data.announcementBrands);
      data.visibilityRoles.forEach((r) => fd.append('visibilityRoles', r));
      if (data.image) fd.append('image', data.image);
      const res = await adminApi.post('/announcements', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error('Error creating announcement:', error);
      throw error?.response?.data || error;
    }
  },

  async updateAnnouncement(id: string, data: {
    announcementTitle?: string;
    announcementText?: string;
    announcementTag?: string[];
    announcementBrands?: string;
    visibilityRoles?: string[];
    image?: File | null;
    removeImage?: boolean;
  }): Promise<any> {
    try {
      const fd = new FormData();
      if (data.announcementTitle) fd.append('announcementTitle', data.announcementTitle);
      if (data.announcementText) fd.append('announcementText', data.announcementText);
      if (data.announcementTag) data.announcementTag.forEach((t) => fd.append('announcementTag', t));
      if (data.announcementBrands) fd.append('announcementBrands', data.announcementBrands);
      if (data.visibilityRoles) data.visibilityRoles.forEach((r) => fd.append('visibilityRoles', r));
      if (data.image) fd.append('image', data.image);
      if (data.removeImage) fd.append('removeImage', 'true');
      const res = await adminApi.patch(`/announcements/${id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error(`Error updating announcement ${id}:`, error);
      throw error?.response?.data || error;
    }
  },

  async deleteAnnouncement(id: string): Promise<void> {
    try {
      await adminApi.delete(`/announcements/${id}`);
    } catch (error: any) {
      console.error(`Error deleting announcement ${id}:`, error);
      throw error?.response?.data || error;
    }
  },

  async getAnnouncementTags(): Promise<any[]> {
    try {
      const res = await adminApi.get('/announcements-tags');
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error('Error fetching announcement tags:', error);
      throw error?.response?.data || error;
    }
  },

  async getAnnouncementTag(tag: string): Promise<any> {
    try {
      const res = await adminApi.get(`/announcements-tags/${encodeURIComponent(tag)}`);
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error(`Error fetching announcement tag ${tag}:`, error);
      throw error?.response?.data || error;
    }
  },

  async subscribeToAnnouncementTag(announcementTag: string, grandparentIds: string[]): Promise<any> {
    try {
      const res = await adminApi.post('/announcements-tags', { announcementTag, grandparentIds });
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error('Error subscribing to announcement tag:', error);
      throw error?.response?.data || error;
    }
  },

  async renameAnnouncementTag(announcementTagId: string, newAnnouncementTag: string): Promise<any> {
    try {
      const res = await adminApi.patch(`/announcements-tags/${encodeURIComponent(announcementTagId)}`, { newAnnouncementTag });
      if (res.status >= 200 && res.status < 300) return res.data;
      throw new Error(res.statusText);
    } catch (error: any) {
      console.error(`Error renaming announcement tag ${announcementTagId}:`, error);
      throw error?.response?.data || error;
    }
  },

  async deleteAnnouncementTag(announcementTagId: string): Promise<void> {
    try {
      await adminApi.delete(`/announcements-tags/${encodeURIComponent(announcementTagId)}`);
    } catch (error: any) {
      console.error(`Error deleting announcement tag ${announcementTagId}:`, error);
      throw error?.response?.data || error;
    }
  },

  // DELETE /announcements-tags/:tag/subscribers/:grandparentId
  async unsubscribeFromAnnouncementTag(announcementTag: string, grandparentId: string): Promise<void> {
    try {
      await adminApi.delete(
        `/announcements-tags/${encodeURIComponent(announcementTag)}/subscribers/${encodeURIComponent(grandparentId)}`,
      );
    } catch (error: any) {
      console.error('Error unsubscribing from announcement tag:', error);
      throw error?.response?.data || error;
    }
  },

  async getMasterPaymentProviders(filterPayload: any = {}): Promise<IPaymentProvider[]> {
    try {
      const payload = filterPayload || {};
      const response = await adminApi.post('/master-payment-providers/getProviders', payload);
      if (response.status >= 200 && response.status < 300) {
        return response.data.data || response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error('Error fetching master payment providers:', error);
      notifyError('Failed to fetch payment providers. Please try again later.');
      throw error?.response?.data || error;
    }
  },

  async getPaymentProviderById(providerId: string): Promise<IPaymentProvider> {
    try {
      const response = await adminApi.get(`/master-payment-providers/${providerId}`);
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error(`Error fetching payment provider ${providerId}:`, error);
      notifyError('Failed to fetch provider details. Please try again later.');
      throw error?.response?.data || error;
    }
  },

  async createPaymentProvider(formData: ICreatePaymentProviderForm): Promise<IPaymentProvider> {
    try {
      const response = await adminApi.post('/master-payment-providers', formData);
      if (response.status >= 200 && response.status < 300) {
        return response.data as any;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error('Error creating payment provider:', error);
      throw error?.response?.data || error;
    }
  },

  async updatePaymentProvider(providerId: string, formData: ICreatePaymentProviderForm): Promise<IPaymentProvider> {
    try {
      const response = await adminApi.put(`/master-payment-providers/${providerId}`, formData);
      if (response.status >= 200 && response.status < 300) {
        return response.data as any;
      }
      throw new Error(response.statusText);
    } catch (error: any) {
      console.error(`Error updating payment provider ${providerId}:`, error);
      throw error?.response?.data || error;
    }
  },

  async getPaymentBrands(filters: Record<string, any> = {}): Promise<any[]> {
    try {
      // Changes made to maintain payment-directory and admin-frontend compatibility
      // Backend route renamed from POST /payment-brands/getBrands to GET /payment-brands
      const response = await adminApi.get('/payment-brands', { params: filters });
      const raw = response.data as any;
      return Array.isArray(raw) ? raw : raw?.data ?? [];
    } catch (err: any) {
      console.error('getPaymentBrands error', err);
      throw err?.response?.data || err;
    }
  },

  async deletePaymentBrand(id: string): Promise<void> {
    try {
      await adminApi.delete(`/payment-brands/${id}`);
    } catch (err: any) {
      console.error(`deletePaymentBrand error for ${id}`, err);
      throw err?.response?.data || err;
    }
  },

  async getPaymentConnectors(filters: Record<string, any> = {}): Promise<any[]> {
    try {
      // Changes made to maintain payment-directory and admin-frontend compatibility
      // Backend route renamed from POST /payment-connectors/getConnectors to GET /payment-connectors
      const response = await adminApi.get('/payment-connectors', { params: filters });
      const raw = response.data as any;
      return Array.isArray(raw) ? raw : raw?.data ?? [];
    } catch (err: any) {
      console.error('getPaymentConnectors error', err);
      throw err?.response?.data || err;
    }
  },

  async deletePaymentConnector(id: string): Promise<void> {
    try {
      await adminApi.delete(`/payment-connectors/${id}`);
    } catch (err: any) {
      console.error(`deletePaymentConnector error for ${id}`, err);
      throw err?.response?.data || err;
    }
  },

  async getConnectorById(id: string): Promise<any> {
    return adminApi
      .get(`/payment-connectors/${id}`)
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`getConnectorById error (${id})`, err);
        throw err;
      });
  },

  async createConnector(data: any): Promise<any> {
    return adminApi
      .post('/payment-connectors', data)
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error('createConnector error', err);
        throw err;
      });
  },

  async updateConnector(id: string, data: any): Promise<any> {
    return adminApi
      .patch(`/payment-connectors/${id}`, data)
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`updateConnector error (${id})`, err);
        throw err;
      });
  },

  async uploadConnectorLogo(id: string, file: File): Promise<{ logoUrl: string }> {
    const formData = new FormData();
    formData.append('logo', file);
    return adminApi
      .patch(`/payment-connectors/${id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`uploadConnectorLogo error (${id})`, err);
        throw err;
      });
  },

  async uploadPaymentBrandLogo(id: string, file: File): Promise<{ logoUrl: string }> {
    const formData = new FormData();
    formData.append('logo', file);
    return adminApi
      .patch(`/payment-brands/${id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res: AxiosResponse<any>) => {
        if (res.status >= 200 && res.status < 300) return res.data;
        throw new Error(res.statusText);
      })
      .catch((err) => {
        console.error(`uploadPaymentBrandLogo error (${id})`, err);
        throw err;
      });
  },

  async getPaymentBrandById(id: string): Promise<any> {
    try {
      const response = await adminApi.get(`/payment-brands/${id}`);
      return response.data;
    } catch (err: any) {
      console.error(`getPaymentBrandById error for ${id}`, err);
      throw err?.response?.data || err;
    }
  },

  async createPaymentBrand(data: Record<string, any>): Promise<any> {
    try {
      const response = await adminApi.post('/payment-brands', data);
      return response.data;
    } catch (err: any) {
      console.error('createPaymentBrand error', err);
      throw err?.response?.data || err;
    }
  },

  async updatePaymentBrand(id: string, data: Record<string, any>): Promise<any> {
    try {
      const response = await adminApi.patch(`/payment-brands/${id}`, data);
      return response.data;
    } catch (err: any) {
      console.error(`updatePaymentBrand error for ${id}`, err);
      throw err?.response?.data || err;
    }
  },
};
